import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './Salary.css';

const POLICY = [
  { icon: '🟡', category: 'Minor Late',          threshold: '10–30 minutes late', deduction: 'Casual leave',      severity: 1 },
  { icon: '🟠', category: 'Major Late',          threshold: '30–60 minutes late', deduction: 'Casual leave',      severity: 2 },
  { icon: '🔶', category: 'Every 3 Lates',       threshold: 'Within one month',   deduction: '½ day salary/leave', severity: 3 },
  { icon: '🔴', category: 'Every 6 Lates',       threshold: 'Within one month',   deduction: '1 day salary/leave', severity: 4 },
  { icon: '⛔', category: 'Unauthorized Absent', threshold: 'No clock-in at all', deduction: '1 day salary',       severity: 5 },
  { icon: '📋', category: 'Auto Casual Leave',   threshold: 'No clock-in, balance available', deduction: 'Casual leave used', severity: 2 },
  { icon: '⏱️', category: 'Grace Period',        threshold: 'Applies to everyone', deduction: '15 minutes',        severity: 0 },
];

export default function Salary({ employee }) {
  const [salary, setSalary]   = useState(null);
  const [loading, setLoading] = useState(true);

  const now   = new Date();
  const month = now.getMonth() + 1;
  const year  = now.getFullYear();

  useEffect(() => { loadSalary(); }, []);

  const loadSalary = async () => {
    try {
      setLoading(true);
      const data = await api.getMySalary(month, year);
      setSalary(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const deductionPct = parseFloat(salary?.deduction_percent || 0);
  const isHealthy     = deductionPct === 0;
  const healthPct     = Math.max(0, Math.min(100, 100 - deductionPct));
  const ringColor     = isHealthy ? '#4ade80' : deductionPct < 10 ? '#fbbf24' : '#f87171';

  const r = 34, circ = 2 * Math.PI * r;
  const ringOffset = circ * (1 - healthPct / 100);

  // Build the breakdown as visual impact items
  const breakdownItems = salary ? [
    {
      icon: '🚫', label: 'Unauthorized Absent',
      value: salary.absent_days > 0 ? `${salary.absent_days} day${salary.absent_days > 1 ? 's' : ''}` : 'None',
      type: salary.absent_days > 0 ? 'bad' : 'neutral',
      show: true,
    },
    {
      icon: '⏱️', label: 'Late Arrivals',
      value: salary.late_count > 0 ? `${salary.late_count} time${salary.late_count > 1 ? 's' : ''}` : 'None',
      type: salary.late_count > 0 ? 'warn' : 'neutral',
      show: true,
    },
    {
      icon: '📋', label: 'Casual Leave Used (Late Policy)',
      value: salary.casual_leaves_consumed > 0 ? `${salary.casual_leaves_consumed} day${salary.casual_leaves_consumed > 1 ? 's' : ''}` : 'None',
      type: salary.casual_leaves_consumed > 0 ? 'warn' : 'neutral',
      show: true,
    },
    {
      icon: '⚠️', label: 'Auto Casual Leave (Absence)',
      value: `${salary.casual_auto_days} day${salary.casual_auto_days > 1 ? 's' : ''} auto-used`,
      type: 'warn',
      show: salary.casual_auto_days > 0,
    },
    {
      icon: '✅', label: 'Approved Leaves',
      value: `${salary.approved_leaves || 0} day${salary.approved_leaves !== 1 ? 's' : ''}`,
      type: 'good',
      show: true,
    },
    {
      icon: '🎉', label: 'Public Holidays',
      value: `${salary.public_holidays} day${salary.public_holidays > 1 ? 's' : ''} — no deduction`,
      type: 'good',
      show: salary.public_holidays > 0,
    },
  ].filter(i => i.show) : [];

  return (
    <div className="salary-page">

      <div className="sal-section-label">
        {now.toLocaleString('default', { month: 'long' })} {year} — Salary Impact
      </div>

      {loading ? (
        <div className="sal-loading"><div className="sal-spinner" /></div>
      ) : salary?.error ? (
        <div className="sal-error-box">{salary.error}</div>
      ) : (
        <>
          {/* Hero — now with a real animated health ring */}
          <div className={`net-banner-v2 ${isHealthy ? 'net-banner-healthy' : 'net-banner-warning'}`}>
            <div className="net-banner-left">
              <div className="net-label">Total Deduction This Month</div>
              <div className="net-amount">{salary?.deduction_percent || '0.00'}%</div>
              <div className="net-sub">
                {salary?.has_salary
                  ? `PKR ${salary.total_deduction || '0.00'} deducted this month`
                  : 'Salary not yet set up by HR'}
              </div>
            </div>
            <div className="net-banner-ring">
              <svg width="76" height="76" viewBox="0 0 76 76">
                <circle cx="38" cy="38" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="7" />
                <circle cx="38" cy="38" r={r} fill="none"
                  stroke={ringColor} strokeWidth="7"
                  strokeDasharray={circ}
                  strokeDashoffset={ringOffset}
                  strokeLinecap="round"
                  transform="rotate(-90 38 38)"
                  style={{ transition: 'stroke-dashoffset 1s ease' }}
                />
                <text x="38" y="34" textAnchor="middle" fontSize="17" fontWeight="800" fill="#fff">{healthPct}%</text>
                <text x="38" y="48" textAnchor="middle" fontSize="8" fill="rgba(255,255,255,0.5)">HEALTHY</text>
              </svg>
            </div>
          </div>

          {/* Stat cards */}
          <div className="salary-stats-v2">
            <div className="sal-stat-v2 sal-stat-amber">
              <div className="sal-stat-v2-icon">🚫</div>
              <div className="sal-stat-v2-num">{salary?.absent_days || 0}</div>
              <div className="sal-stat-v2-lbl">Unauthorized Absent</div>
              <div className="sal-stat-v2-sub">salary deducted</div>
            </div>
            <div className="sal-stat-v2 sal-stat-red">
              <div className="sal-stat-v2-icon">⏱️</div>
              <div className="sal-stat-v2-num">{salary?.late_count || 0}</div>
              <div className="sal-stat-v2-lbl">Late Count</div>
              <div className="sal-stat-v2-sub">this month</div>
            </div>
          </div>

          {/* ═══ BREAKDOWN — reimagined as visual impact cards ═══ */}
          <div className="sal-section-label" style={{ marginTop: 18 }}>Breakdown</div>
          <div className="sal-impact-list">
            {breakdownItems.map((item, i) => (
              <div key={i} className={`sal-impact-card sal-impact-${item.type}`}>
                <div className="sal-impact-icon">{item.icon}</div>
                <div className="sal-impact-label">{item.label}</div>
                <div className="sal-impact-value">{item.value}</div>
              </div>
            ))}
          </div>

          {/* Total summary bar */}
          <div className="sal-total-bar">
            <span className="sal-total-lbl">Total Deduction</span>
            <span className="sal-total-val">{salary?.deduction_percent || '0.00'}% of salary</span>
          </div>

          {/* ═══ POLICY REFERENCE — reimagined as an escalation ladder ═══ */}
          <div className="sal-section-label" style={{ marginTop: 20 }}>Policy Reference</div>
          <div className="sal-policy-ladder">
            {POLICY.map((p, i) => (
              <div key={i} className={`sal-policy-card-v2 sal-sev-${p.severity}`}>
                <div className="sal-policy-icon-badge">{p.icon}</div>
                <div className="sal-policy-info">
                  <div className="sal-policy-name">{p.category}</div>
                  <div className="sal-policy-thresh">{p.threshold}</div>
                </div>
                <div className="sal-policy-penalty">{p.deduction}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}